import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import RestoreIcon from "@mui/icons-material/SettingsBackupRestoreOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CSSProperties, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { financeApi } from "../../lib/api";
import { DatePickerField } from "../../components/DatePickerField";
import { AccountSelect } from "../../components/FinanceSelects";
import { StepChips } from "../../components/StepChips";
import { CategoryIcon, money } from "../../lib/format";
import type {
  Account,
  Category,
  Currency,
  Group,
  ImportApplyResult,
  ImportBatch,
  ImportEntityPreviewRow,
  ImportEntityUpdatePayload,
  ImportProvider,
  ImportRowUpdatePayload,
  Member,
  Tag,
  TransactionType,
} from "../../lib/types";
import { queryKeys } from "../../lib/queryKeys";
import { ImportRowFilter, ImportRowFilterCriteria, ImportSection, ProviderIcon, ProviderOption, RollbackSections, StickyFilteredRows, accountHistoryLabel, appliedCount, batchStatusColor, batchStatusLabel, currencySymbol, defaultImportRowsPerPage, formatBatchDate, formatImportEta, formatProgressPercent, importBatchTimestamp, importRowFilterKey, importRowFilterLabel, importRowFilterValue, importRowMatchesFilters, importRowsPerPageOptions, importableRowCount, importedRowCount, isAibrosFileProvider, isRevolutProvider, isBatchApplied, isBatchCancelled, isBatchCompleted, isBatchFailed, isBatchPolling, maxStatementUploadBytes, maxStatementUploadMegabytes, missingCurrencyCode, progressValue, providerLabel, providerOptions, recentBatchActionLabel, rowCount, skippedCount, transactionTypeLabels } from "./importHelpers";
import { CurrencyPreviewList, EntityPreviewList } from "./EntityPreview";
import { ImportInstructions } from "./ImportInstructions";
import { ImportRowEditor } from "./ImportRowEditor";
import { useImportJobPolling } from "./useImportJobPolling";
import { SidePanel } from "../../components/SidePanel";
type ImportRecordsModalProps = {
  group: Group;
  accounts: Account[];
  members: Member[];
  categories: Category[];
  tags: Tag[];
  currencies: Currency[];
  onClose: () => void;
};

export function ImportRecordsModal({ group, accounts, members, categories, tags, currencies, onClose }: ImportRecordsModalProps) {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState<ImportProvider | "">("");
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [includeRecords, setIncludeRecords] = useState(true);
  const [includeCategories, setIncludeCategories] = useState(false);
  const [includeAccounts, setIncludeAccounts] = useState(false);
  const [includeCurrencies, setIncludeCurrencies] = useState(false);
  const [activeSection, setActiveSection] = useState<ImportSection>("records");
  const [batchId, setBatchId] = useState("");
  const [status, setStatus] = useState("");
  const [appliedResult, setAppliedResult] = useState<ImportApplyResult | null>(null);
  const [applyRequested, setApplyRequested] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<ImportBatch | null>(null);
  const [rollbackSections, setRollbackSections] = useState<RollbackSections>({ records: true, categories: false, accounts: false });
  const [rowFilter, setRowFilter] = useState<ImportRowFilter>("all");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<TransactionType | "">("");
  const [searchDateFrom, setSearchDateFrom] = useState("");
  const [searchDateTo, setSearchDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultImportRowsPerPage);
  const [stickyFilteredRows, setStickyFilteredRows] = useState<StickyFilteredRows>({ filterKey: "", rowIds: [] });
  const previewScrollRef = useRef<HTMLDivElement | null>(null);
  const enabledCurrencyCodes = useMemo(() => new Set(currencies.map((currency) => currency.code)), [currencies]);

  const importBatchesQuery = useQuery({
    queryKey: queryKeys.importBatches(group.id),
    queryFn: () => financeApi.importBatches(group.id, { limit: 100 }),
    retry: false,
    staleTime: 15_000,
  });

  const batchQuery = useQuery({
    queryKey: queryKeys.importBatch(group.id, batchId),
    queryFn: () => financeApi.importBatch(group.id, batchId),
    enabled: Boolean(batchId),
  });

  const batch = batchQuery.data;
  useImportJobPolling({ groupId: group.id, batchId, batch, setAppliedResult });
  const displayBatch = appliedResult || batch;
  const providerSelected = Boolean(provider);
  const fileProviderSelected = isAibrosFileProvider(provider);
  const revolutProviderSelected = isRevolutProvider(provider);
  const currentBatchIsFileImport = isAibrosFileProvider(displayBatch?.provider || provider);
  const currentImportSourceAccountId = displayBatch?.source_account_id || sourceAccountId;
  const selectedSourceAccount = accounts.find((account) => account.id === currentImportSourceAccountId);
  const importFileName =
    displayBatch?.original_filename ||
    file?.name ||
    (fileProviderSelected ? "Finance file" : revolutProviderSelected ? "Statement XLSX" : "Statement PDF");
  const recentBatches = useMemo(() => {
    return [...(importBatchesQuery.data || [])].sort((a, b) => {
      const left = importBatchTimestamp(a);
      const right = importBatchTimestamp(b);
      if (!left && !right) return 0;
      if (!left) return 1;
      if (!right) return -1;
      return new Date(right).getTime() - new Date(left).getTime();
    });
  }, [importBatchesQuery.data]);
  const recentPreviewBatches = recentBatches.slice(0, 3);
  const rows = displayBatch?.rows || [];
  const categoryRows = displayBatch?.category_rows || [];
  const accountRows = displayBatch?.account_rows || [];
  const currencyRows = displayBatch?.currency_rows || [];
  const availableSections = useMemo<ImportSection[]>(() => {
    if (currentBatchIsFileImport) {
      const sections: ImportSection[] = [];
      if (rows.length) sections.push("records");
      if (categoryRows.length) sections.push("categories");
      if (accountRows.length) sections.push("accounts");
      if (currencyRows.length) sections.push("currencies");
      return sections.length ? sections : ["records"];
    }
    const sections: ImportSection[] = [];
    if (rows.length) sections.push("records");
    if (categoryRows.length) sections.push("categories");
    if (accountRows.length) sections.push("accounts");
    return sections.length ? sections : ["records"];
  }, [accountRows.length, categoryRows.length, currencyRows.length, currentBatchIsFileImport, rows.length]);
  const progress = progressValue(batch);
  const progressPercent = formatProgressPercent(progress);
  const progressEta = formatImportEta(batch, progress);
  const importableRows = importableRowCount(rows);
  const importedRows = importedRowCount(rows);
  const totalRows = rows.length || (batch ? rowCount(batch) : null);
  const filterCriteria = useMemo<ImportRowFilterCriteria>(
    () => ({ rowFilter, searchQuery, searchType, searchDateFrom, searchDateTo }),
    [rowFilter, searchDateFrom, searchDateTo, searchQuery, searchType],
  );
  const hasSearchFilters = Boolean(searchQuery || searchType || searchDateFrom || searchDateTo);
  const filterKey = useMemo(() => importRowFilterKey(batchId, filterCriteria), [batchId, filterCriteria]);
  const matchingRowIds = useMemo(
    () => rows.filter((row) => importRowMatchesFilters(row, filterCriteria)).map((row) => row.id),
    [filterCriteria, rows],
  );
  const filteredRows = useMemo(() => {
    const rowIds = stickyFilteredRows.filterKey === filterKey ? stickyFilteredRows.rowIds : matchingRowIds;
    const visibleIds = new Set(rowIds);
    return rows.filter((row) => visibleIds.has(row.id));
  }, [filterKey, matchingRowIds, rows, stickyFilteredRows]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const visiblePage = Math.min(currentPage, totalPages);
  const paginatedRows = filteredRows.slice((visiblePage - 1) * rowsPerPage, visiblePage * rowsPerPage);
  const rowFilterCounts = useMemo(() => {
    const counts: Record<ImportRowFilter, number> = { all: rows.length, ready: 0, review: 0, duplicate: 0, ignored: 0 };
    for (const row of rows) counts[importRowFilterValue(row)] += 1;
    return counts;
  }, [rows]);
  const amountColumnCh = useMemo(() => {
    let widest = 7;
    for (const row of rows) {
      const length = `${currencySymbol(row.currency)}${money(row.amount)}`.length + 1;
      if (length > widest) widest = length;
    }
    return widest;
  }, [rows]);
  const isProcessingImport = Boolean(batchId && isBatchPolling(batch?.status));
  const canApply = Boolean(batchId && importableRows && !isProcessingImport && !isBatchFailed(batch?.status) && !isBatchCompleted(batch?.status) && !appliedResult);
  const canChangeImportSourceAccount = Boolean(batchId && rows.length && batch?.status === "ready" && !appliedResult && !currentBatchIsFileImport);
  const completedAppliedCount = appliedResult ? appliedCount(appliedResult, rows) : 0;
  const completedSkippedCount = appliedResult ? skippedCount(appliedResult) : null;

  useEffect(() => {
    if (batch?.source_account_id) setSourceAccountId(batch.source_account_id);
    if (providerOptions.some((option) => option.value === batch?.provider)) {
      setProvider(batch?.provider as ImportProvider);
    }
  }, [batch?.provider, batch?.source_account_id]);

  useEffect(() => {
    if (!availableSections.includes(activeSection)) {
      setActiveSection(availableSections[0] || "records");
    }
  }, [activeSection, availableSections]);

  useEffect(() => {
    setStickyFilteredRows((current) => {
      const existingRowIds = new Set(rows.map((row) => row.id));
      if (current.filterKey !== filterKey) {
        return { filterKey, rowIds: matchingRowIds };
      }
      const nextRowIds = current.rowIds.filter((rowId) => existingRowIds.has(rowId));
      const nextRowIdSet = new Set(nextRowIds);
      for (const rowId of matchingRowIds) {
        if (!nextRowIdSet.has(rowId)) {
          nextRowIds.push(rowId);
          nextRowIdSet.add(rowId);
        }
      }
      if (nextRowIds.length === current.rowIds.length && nextRowIds.every((rowId, index) => rowId === current.rowIds[index])) {
        return current;
      }
      return { filterKey, rowIds: nextRowIds };
    });
  }, [filterKey, matchingRowIds, rows]);

  useEffect(() => {
    setCurrentPage(1);
  }, [batchId, rowFilter, searchQuery, searchType, searchDateFrom, searchDateTo, rowsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const changePage = (nextPage: number) => {
    setCurrentPage(nextPage);
    previewScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!provider) throw new Error("Select statement provider.");
      if (!file) throw new Error(fileProviderSelected ? "Select an xlsx or csv file." : revolutProviderSelected ? "Select an xlsx file." : "Select a PDF file.");
      if (file.size > maxStatementUploadBytes) {
        throw new Error(`Import file must be ${maxStatementUploadMegabytes} MB or smaller.`);
      }
      if (fileProviderSelected) {
        if (!includeRecords && !includeCategories && !includeAccounts && !includeCurrencies) throw new Error("Select at least one import section.");
        return financeApi.createFileImport(group.id, { file, includeRecords, includeCategories, includeAccounts, includeCurrencies, asyncProcessing: true });
      }
      if (!sourceAccountId) throw new Error("Select a source account.");
      return financeApi.createStatementImport(group.id, { file, provider, sourceAccountId, asyncProcessing: true });
    },
    onSuccess: (nextBatch) => {
      setStatus("");
      setAppliedResult(null);
      setBatchId(nextBatch.id);
      queryClient.setQueryData(queryKeys.importBatch(group.id, nextBatch.id), nextBatch);
      void queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) });
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const updateRowMutation = useMutation({
    mutationFn: ({ rowId, payload }: { rowId: string; payload: ImportRowUpdatePayload }) =>
      financeApi.updateImportRow(group.id, batchId, rowId, payload),
    onMutate: async ({ rowId, payload }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.importBatch(group.id, batchId) });
      const previousBatch = queryClient.getQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId));
      queryClient.setQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId), (current) => {
        if (!current?.rows) return current;
        return {
          ...current,
          rows: current.rows.map((row) => (row.id === rowId ? { ...row, ...payload } : row)),
        };
      });
      return { previousBatch };
    },
    onSuccess: async (nextBatch) => {
      queryClient.setQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId), (current) => mergeImportBatchPatch(current, nextBatch));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) }),
      ]);
    },
    onError: (error, _variables, context) => {
      if (context?.previousBatch) {
        queryClient.setQueryData(queryKeys.importBatch(group.id, batchId), context.previousBatch);
      }
      setStatus(error instanceof Error ? error.message : "Request failed");
    },
  });

  const addCurrencyMutation = useMutation({
    mutationFn: (targetCode: string) => financeApi.createCurrency(group.id, targetCode),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["currencies"] });
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const updateEntityMutation = useMutation({
    mutationFn: ({ entityId, payload }: { entityId: string; payload: ImportEntityUpdatePayload }) =>
      financeApi.updateImportEntity(group.id, batchId, entityId, payload),
    onMutate: async ({ entityId, payload }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.importBatch(group.id, batchId) });
      const previousBatch = queryClient.getQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId));
      queryClient.setQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId), (current) => {
        if (!current) return current;
        const updateEntity = (entity: ImportEntityPreviewRow) => (entity.id === entityId ? { ...entity, ...payload } : entity);
        return {
          ...current,
          category_rows: current.category_rows?.map(updateEntity),
          account_rows: current.account_rows?.map(updateEntity),
        };
      });
      return { previousBatch };
    },
    onSuccess: async (nextBatch) => {
      queryClient.setQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId), (current) => mergeImportBatchPatch(current, nextBatch));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["accounts"] }),
        queryClient.invalidateQueries({ queryKey: ["categories"] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) }),
      ]);
    },
    onError: (error, _variables, context) => {
      if (context?.previousBatch) {
        queryClient.setQueryData(queryKeys.importBatch(group.id, batchId), context.previousBatch);
      }
      setStatus(error instanceof Error ? error.message : "Request failed");
    },
  });

  const updateSourceAccountMutation = useMutation({
    mutationFn: (nextSourceAccountId: string) => financeApi.updateImportBatchSourceAccount(group.id, batchId, nextSourceAccountId),
    onMutate: async (nextSourceAccountId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.importBatch(group.id, batchId) });
      const previousBatch = queryClient.getQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId));
      setSourceAccountId(nextSourceAccountId);
      queryClient.setQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId), (current) => {
        if (!current) return current;
        return {
          ...current,
          source_account_id: nextSourceAccountId,
          rows: current.rows?.map((row) => {
            if (["imported", "error"].includes(String(row.status || "").toLowerCase())) return row;
            return {
              ...row,
              account_id: nextSourceAccountId,
              transfer_account_id: row.transfer_account_id === nextSourceAccountId ? row.account_id || null : row.transfer_account_id,
            };
          }),
        };
      });
      return { previousBatch };
    },
    onSuccess: async (nextBatch) => {
      setStatus("");
      setSourceAccountId(nextBatch.source_account_id || "");
      queryClient.setQueryData(queryKeys.importBatch(group.id, batchId), nextBatch);
      await queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) });
    },
    onError: (error, _nextSourceAccountId, context) => {
      if (context?.previousBatch) {
        queryClient.setQueryData(queryKeys.importBatch(group.id, batchId), context.previousBatch);
        setSourceAccountId(context.previousBatch.source_account_id || "");
      }
      setStatus(error instanceof Error ? error.message : "Request failed");
    },
  });

  const applyMutation = useMutation({
    mutationFn: () => financeApi.applyImportBatch(group.id, batchId, { asyncProcessing: true }),
    onMutate: () => setApplyRequested(true),
    onSuccess: async (result) => {
      setStatus("");
      queryClient.removeQueries({ queryKey: queryKeys.importBatchStatus(group.id, batchId) });
      queryClient.setQueryData<ImportBatch>(queryKeys.importBatch(group.id, batchId), (current) => mergeImportBatchPatch(current, result));
      await queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) });
    },
    onError: (error) => {
      setApplyRequested(false);
      setStatus(error instanceof Error ? error.message : "Request failed");
    },
  });

  const isApplyingImport = applyMutation.isPending || (applyRequested && isBatchPolling(batch?.status));

  useEffect(() => {
    if (!applyRequested || applyMutation.isPending || isBatchPolling(batch?.status)) return;
    setApplyRequested(false);
  }, [applyMutation.isPending, applyRequested, batch?.status]);

  const stopMutation = useMutation({
    mutationFn: () => financeApi.stopImportBatch(group.id, batchId),
    onSuccess: async (nextBatch) => {
      setStatus("");
      queryClient.setQueryData(queryKeys.importBatch(group.id, batchId), nextBatch);
      await queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) });
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const deleteBatchMutation = useMutation({
    mutationFn: (deleteBatchId: string) => financeApi.deleteImportBatch(group.id, deleteBatchId),
    onMutate: async (deleteBatchId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.importBatches(group.id) });
      const previousBatches = queryClient.getQueryData<ImportBatch[]>(queryKeys.importBatches(group.id));
      queryClient.setQueryData<ImportBatch[]>(queryKeys.importBatches(group.id), (current) =>
        current ? current.filter((item) => item.id !== deleteBatchId) : current,
      );
      return { previousBatches };
    },
    onSuccess: async (_result, deletedBatchId) => {
      queryClient.removeQueries({ queryKey: queryKeys.importBatch(group.id, deletedBatchId) });
      if (batchId === deletedBatchId) {
        setBatchId("");
        setAppliedResult(null);
      }
      setStatus("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.records }),
        queryClient.invalidateQueries({ queryKey: queryKeys.deletedRecords }),
        queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
        queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) }),
      ]);
    },
    onError: (error, _deletedBatchId, context) => {
      if (context?.previousBatches) {
        queryClient.setQueryData(queryKeys.importBatches(group.id), context.previousBatches);
      }
      setStatus(error instanceof Error ? error.message : "Request failed");
    },
  });

  const rollbackBatchMutation = useMutation({
    mutationFn: ({ rollbackBatchId, sections }: { rollbackBatchId: string; sections: RollbackSections }) =>
      financeApi.rollbackImportBatch(group.id, rollbackBatchId, sections),
    onSuccess: async (nextBatch) => {
      setStatus("");
      if (batchId === nextBatch.id) {
        setAppliedResult(null);
        queryClient.setQueryData(queryKeys.importBatch(group.id, nextBatch.id), nextBatch);
      } else {
        queryClient.setQueryData(queryKeys.importBatch(group.id, nextBatch.id), nextBatch);
      }
      setRollbackTarget(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.records }),
        queryClient.invalidateQueries({ queryKey: queryKeys.deletedRecords }),
        queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
        queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) }),
      ]);
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const deleteAllBatchesMutation = useMutation({
    mutationFn: async (deleteBatchIds: string[]) => {
      await Promise.all(deleteBatchIds.map((deleteBatchId) => financeApi.deleteImportBatch(group.id, deleteBatchId)));
      return deleteBatchIds;
    },
    onMutate: async (deleteBatchIds) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.importBatches(group.id) });
      const previousBatches = queryClient.getQueryData<ImportBatch[]>(queryKeys.importBatches(group.id));
      const deleteBatchIdSet = new Set(deleteBatchIds);
      queryClient.setQueryData<ImportBatch[]>(queryKeys.importBatches(group.id), (current) =>
        current ? current.filter((item) => !deleteBatchIdSet.has(item.id)) : current,
      );
      return { previousBatches };
    },
    onSuccess: async (deletedBatchIds) => {
      for (const deletedBatchId of deletedBatchIds) {
        queryClient.removeQueries({ queryKey: queryKeys.importBatch(group.id, deletedBatchId) });
      }
      if (deletedBatchIds.includes(batchId)) {
        setBatchId("");
        setAppliedResult(null);
      }
      setStatus("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.records }),
        queryClient.invalidateQueries({ queryKey: queryKeys.deletedRecords }),
        queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
        queryClient.invalidateQueries({ queryKey: queryKeys.importBatches(group.id) }),
      ]);
    },
    onError: (error, _deletedBatchIds, context) => {
      if (context?.previousBatches) {
        queryClient.setQueryData(queryKeys.importBatches(group.id), context.previousBatches);
      }
      setStatus(error instanceof Error ? error.message : "Request failed");
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus("");
    uploadMutation.mutate();
  };

  const selectBatch = (nextBatch: ImportBatch) => {
    setStatus("");
    setAppliedResult(null);
    setRowFilter("all");
    setCurrentPage(1);
    setBatchId(nextBatch.id);
    setHistoryDialogOpen(false);
    if (nextBatch.source_account_id) setSourceAccountId(nextBatch.source_account_id);
    if (providerOptions.some((option) => option.value === nextBatch.provider)) {
      setProvider(nextBatch.provider as ImportProvider);
    }
  };

  const deleteBatch = (deleteBatch: ImportBatch) => {
    if (!window.confirm(`Delete import record from ${formatBatchDate(deleteBatch)}? Imported records will stay in history.`)) return;
    deleteBatchMutation.mutate(deleteBatch.id);
  };

  const rollbackBatch = (rollbackBatch: ImportBatch) => {
    setRollbackTarget(rollbackBatch);
    setRollbackSections({
      records: true,
      categories: Boolean(rollbackBatch.imported_categories_count || rollbackBatch.category_rows?.length),
      accounts: Boolean(rollbackBatch.imported_accounts_count || rollbackBatch.account_rows?.length),
    });
  };

  const deleteAllBatches = () => {
    if (!recentBatches.length) return;
    if (!window.confirm(`Delete all ${recentBatches.length} import history items?`)) return;
    deleteAllBatchesMutation.mutate(recentBatches.map((recentBatch) => recentBatch.id));
  };

  const addCurrencyAndRevalidateRows = async (code: string) => {
    setStatus("");
    const targetCode = code.toUpperCase();
    const affectedRows = rows.filter((row) => missingCurrencyCode(row) === targetCode);
    try {
      if (!enabledCurrencyCodes.has(targetCode)) {
        await addCurrencyMutation.mutateAsync(targetCode);
      }
      await queryClient.invalidateQueries({ queryKey: ["currencies"] });
      for (const row of affectedRows) {
        await updateRowMutation.mutateAsync({ rowId: row.id, payload: { status: "pending" } });
      }
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Request failed");
    }
  };

  return (
    <>
    <SidePanel
      ariaLabelledBy="importRecordsTitle"
      className="import-side-panel"
      contentClassName={`import-side-panel-content${!batchId ? " import-side-panel-content--setup" : ""}`}
      dataTestId="import-side-panel"
      onClose={onClose}
    >
      {({ close }) => (
        <>
        <Box className="records-modal-toolbar">
          <h2 id="importRecordsTitle">Import records</h2>
          <StepChips
            activeStep={appliedResult || isBatchCompleted(batch?.status) ? 2 : batchId ? 1 : 0}
            ariaLabel="Import progress"
            className="import-steps"
            steps={["1 File", "2 Review", "3 Apply"]}
          />
          <Box className="import-toolbar-actions">
            {batchId ? (
              <Button
                size="small"
                variant="text"
                type="button"
                disabled={uploadMutation.isPending || updateRowMutation.isPending || updateEntityMutation.isPending || updateSourceAccountMutation.isPending || applyMutation.isPending || stopMutation.isPending}
                onClick={() => {
                  setBatchId("");
                  setAppliedResult(null);
                  setStatus("");
                }}
              >
                Back
              </Button>
            ) : null}
            <Button
              size="small"
              variant="outlined"
              type="button"
              startIcon={<HistoryIcon fontSize="small" />}
              onClick={() => setHistoryDialogOpen(true)}
            >
              History
            </Button>
            <IconButton className="records-icon-button" type="button" aria-label="Close import dialog" title="Close" onClick={close}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </IconButton>
          </Box>
        </Box>

        {!appliedResult && !batchId ? (
          <Box className="import-panel-scroll">
          {recentPreviewBatches.length ? (
            <Box className="import-recent-panel">
              <Box className="import-recent-heading">
                <strong>Recent imports</strong>
              </Box>
              <Box className="import-recent-list" aria-label="Recent imports">
                {recentPreviewBatches.map((recentBatch, index) => {
                  const count = rowCount(recentBatch);
                  const sourceAccount = accounts.find((account) => account.id === recentBatch.source_account_id);
                  return (
                    <Button
                      key={recentBatch.id}
                      className="import-recent-card"
                      variant="outlined"
                      type="button"
                      disabled={deleteBatchMutation.isPending || deleteAllBatchesMutation.isPending}
                      onClick={() => selectBatch(recentBatch)}
                    >
                      <span className="import-recent-copy">
                        <span>{recentBatchActionLabel(recentBatch, index === 0)}</span>
                        <small>
                          {formatBatchDate(recentBatch)}
                          {count !== null ? ` · ${count} rows` : ""}
                        </small>
                        <small>
                          {recentBatch.original_filename || providerLabel(recentBatch.provider)}
                          {sourceAccount ? ` · ${accountHistoryLabel(sourceAccount, members)}` : ""}
                        </small>
                      </span>
                      <Chip className="import-recent-status" size="small" color={batchStatusColor(recentBatch)} label={batchStatusLabel(recentBatch)} />
                    </Button>
                  );
                })}
              </Box>
            </Box>
          ) : null}
          <form className="records-form import-form" noValidate onSubmit={submit}>
            <Box className="import-form-grid">
              <TextField
                select
                className="import-provider-field"
                label="Provider"
                value={provider}
                disabled={uploadMutation.isPending || Boolean(batchId)}
                slotProps={{
                  inputLabel: { shrink: true },
                  select: {
                    displayEmpty: true,
                    renderValue: (value) => value
                      ? <ProviderOption value={String(value)} label={providerLabel(String(value))} />
                      : <span className="import-provider-placeholder">Select statement provider</span>,
                  },
                }}
                onChange={(event) => {
                  const nextProvider = event.target.value as ImportProvider;
                  setProvider(nextProvider);
                  setFile(null);
                  setStatus("");
                  if (isAibrosFileProvider(nextProvider)) {
                    setIncludeRecords(true);
                    setIncludeCategories(true);
                    setIncludeAccounts(true);
                    setIncludeCurrencies(true);
                  }
                }}
              >
                <MenuItem value="" disabled>
                  Select statement provider
                </MenuItem>
                {providerOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <ProviderOption value={option.value} label={option.label} />
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title={fileProviderSelected ? "Source account will be taken from file." : ""}>
                <span className="import-source-account-tooltip">
                  <AccountSelect
                    label="Source account"
                    accounts={accounts.filter((account) => !account.is_archived || account.id === sourceAccountId)}
                    members={members}
                    emptyLabel="Select account"
                    value={sourceAccountId}
                    disabled={uploadMutation.isPending || Boolean(batchId) || !providerSelected || fileProviderSelected}
                    onChange={setSourceAccountId}
                  />
                </span>
              </Tooltip>
            </Box>
            {!batchId && provider ? <ImportInstructions provider={provider} /> : null}
            {fileProviderSelected ? (
              <Box className="import-file-options">
                <FormControlLabel control={<Switch checked={includeRecords} onChange={(event) => setIncludeRecords(event.target.checked)} />} label="records" />
                <FormControlLabel control={<Switch checked={includeCategories} onChange={(event) => setIncludeCategories(event.target.checked)} />} label="categories" />
                <FormControlLabel control={<Switch checked={includeAccounts} onChange={(event) => setIncludeAccounts(event.target.checked)} />} label="accounts" />
                <FormControlLabel control={<Switch checked={includeCurrencies} onChange={(event) => setIncludeCurrencies(event.target.checked)} />} label="currencies" />
              </Box>
            ) : null}
            <Box className="import-file-row">
              <input
                id="statementPdf"
                className="import-file-input"
                type="file"
                accept={!provider ? undefined : fileProviderSelected ? ".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : revolutProviderSelected ? ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf"}
                disabled={uploadMutation.isPending || Boolean(batchId) || !providerSelected}
                onChange={(event) => {
                  const nextFile = event.target.files?.[0] || null;
                  setFile(nextFile);
                  if (nextFile && /\.(xlsx|xls)$/i.test(nextFile.name)) {
                    setIncludeRecords(true);
                    setIncludeCategories(true);
                    setIncludeAccounts(true);
                    setIncludeCurrencies(true);
                  } else if (nextFile && /\.csv$/i.test(nextFile.name)) {
                    setIncludeRecords(true);
                    setIncludeCategories(false);
                    setIncludeAccounts(false);
                    setIncludeCurrencies(false);
                  }
                  setStatus(
                    nextFile && nextFile.size > maxStatementUploadBytes
                      ? `Import file must be ${maxStatementUploadMegabytes} MB or smaller.`
                      : "",
                  );
                }}
              />
              <label className={`import-file-picker${uploadMutation.isPending || batchId || !providerSelected ? " disabled" : ""}`} htmlFor="statementPdf">
                <UploadFileIcon fontSize="small" />
                <span>{!provider ? "Select statement provider" : file ? file.name : fileProviderSelected ? "Select xlsx or csv file" : revolutProviderSelected ? "Select xlsx statement" : "Select PDF statement"}</span>
              </label>
              <Button variant="contained" type="submit" disabled={uploadMutation.isPending || Boolean(batchId) || !providerSelected}>
                Start import
              </Button>
            </Box>
          </form>
          </Box>
        ) : null}

        {batchId ? (
          <Box className="import-status-panel">
            <Box className="import-summary-strip">
              <span className="import-summary-item">
                <span className="import-provider-logo" aria-hidden="true">
                  <ProviderIcon value={displayBatch?.provider || provider} />
                </span>
                <span className="import-summary-copy">
                  <small>Provider</small>
                  <strong>{providerLabel(displayBatch?.provider || provider)}</strong>
                </span>
              </span>
              <span className="import-summary-item import-summary-account">
                {canChangeImportSourceAccount ? (
                  <AccountSelect
                    className="import-summary-account-field"
                    size="small"
                    label="Source"
                    accounts={accounts.filter((account) => !account.is_archived || account.id === currentImportSourceAccountId)}
                    members={members}
                    emptyLabel="Select account"
                    value={currentImportSourceAccountId}
                    disabled={updateRowMutation.isPending || updateSourceAccountMutation.isPending || applyMutation.isPending || stopMutation.isPending}
                    onChange={(next) => {
                      if (!next || next === currentImportSourceAccountId) return;
                      setStatus("");
                      updateSourceAccountMutation.mutate(next);
                    }}
                  />
                ) : (
                  <>
                    {selectedSourceAccount ? <CategoryIcon icon={selectedSourceAccount.icon} color={selectedSourceAccount.color} /> : null}
                    <span className="import-summary-copy">
                      <small>Source</small>
                      <strong>{currentBatchIsFileImport ? "From file" : selectedSourceAccount?.name || "Selected account"}</strong>
                    </span>
                  </>
                )}
              </span>
              <span className="import-summary-item">
                <UploadFileIcon fontSize="small" />
                <span className="import-summary-copy">
                  <small>File</small>
                  <strong>{importFileName}</strong>
                </span>
              </span>
              {!appliedResult && !isBatchCompleted(batch?.status) && !isProcessingImport && rowFilterCounts.review ? (
                <Chip
                  className="import-review-chip"
                  size="small"
                  clickable
                  label={`${rowFilterCounts.review} need review`}
                  onClick={() => {
                    setActiveSection("records");
                    setRowFilter("review");
                  }}
                />
              ) : null}
            </Box>
            {batchQuery.isLoading || isProcessingImport || isBatchFailed(batch?.status) ? (
            <Box className="import-status-line">
              <Chip
                size="small"
                label={batchStatusLabel(batch, batchQuery.isLoading ? "loading" : "pending")}
                color={batchStatusColor(batch)}
              />
              {totalRows !== null ? (
                <span>
                  {isBatchCompleted(batch?.status) || appliedResult ? importedRows : importableRows} / {totalRows} {isBatchCompleted(batch?.status) || appliedResult ? "imported" : "records found"}
                </span>
              ) : null}
              {isProcessingImport ? (
                <Button
                  className="import-stop-button"
                  size="small"
                  variant="outlined"
                  color="error"
                  type="button"
                  disabled={stopMutation.isPending}
                  onClick={() => {
                    setStatus("");
                    stopMutation.mutate();
                  }}
                >
                  stop import
                </Button>
              ) : null}
            </Box>
            ) : null}
            {isProcessingImport ? (progress !== null ? <LinearProgress variant="determinate" value={progress} /> : <LinearProgress />) : null}
            {isProcessingImport && (progressPercent || progressEta) ? (
              <Box className="import-progress-meta">
                {progressPercent ? <span className="import-progress-percent">{progressPercent}</span> : null}
                {progressEta ? <span className="import-progress-eta">{progressEta}</span> : null}
              </Box>
            ) : null}
            {batch?.error && !isBatchCancelled(batch) ? <p className="status error">{batch.error}</p> : null}
          </Box>
        ) : null}

        {batchId && availableSections.length > 1 ? (
          <ToggleButtonGroup
            className="import-section-tabs"
            size="small"
            exclusive
            value={activeSection}
            aria-label="Import sections"
            onChange={(_, value) => {
              if (value) setActiveSection(value as ImportSection);
            }}
          >
            {availableSections.includes("records") ? <ToggleButton value="records">Records {rows.length}</ToggleButton> : null}
            {availableSections.includes("categories") ? <ToggleButton value="categories">Categories {categoryRows.length}</ToggleButton> : null}
            {availableSections.includes("accounts") ? <ToggleButton value="accounts">Accounts {accountRows.length}</ToggleButton> : null}
            {availableSections.includes("currencies") ? <ToggleButton value="currencies">Currencies {currencyRows.length}</ToggleButton> : null}
          </ToggleButtonGroup>
        ) : null}

        {batchId ? (
        <Box ref={previewScrollRef} className="import-panel-scroll">
        {activeSection === "categories" ? (
          <EntityPreviewList
            title="Categories"
            rows={categoryRows}
            kind="categories"
            categories={categories}
            members={members}
            isUpdating={updateEntityMutation.isPending || applyMutation.isPending || stopMutation.isPending}
            isBatchApplied={Boolean(appliedResult || isBatchCompleted(batch?.status))}
            onUpdate={(entityId, payload) => {
              setStatus("");
              updateEntityMutation.mutate({ entityId, payload });
            }}
          />
        ) : null}
        {activeSection === "accounts" ? (
          <EntityPreviewList
            title="Accounts"
            rows={accountRows}
            kind="accounts"
            categories={categories}
            members={members}
            isUpdating={updateEntityMutation.isPending || applyMutation.isPending || stopMutation.isPending}
            isBatchApplied={Boolean(appliedResult || isBatchCompleted(batch?.status))}
            onUpdate={(entityId, payload) => {
              setStatus("");
              updateEntityMutation.mutate({ entityId, payload });
            }}
          />
        ) : null}
        {activeSection === "currencies" ? <CurrencyPreviewList rows={currencyRows} /> : null}

        {rows.length && activeSection === "records" ? (
          <Box className="import-preview">
            <Box className="import-filter-block">
            <Box className="import-filter-row">
              <ToggleButtonGroup
                className="import-filter-toggle"
                size="small"
                exclusive
                value={rowFilter}
                aria-label="Filter import rows"
                onChange={(_, value) => {
                  if (value) setRowFilter(value as ImportRowFilter);
                }}
              >
                {(["all", "ready", "review", "duplicate", "ignored"] as ImportRowFilter[]).map((filter) => (
                  <ToggleButton key={filter} className={`import-filter-${filter}`} value={filter} aria-label={`Show ${importRowFilterLabel(filter)} import rows`}>
                    {importRowFilterLabel(filter)} {rowFilterCounts[filter]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Box className={`import-search-actions${hasSearchFilters ? " has-filters" : ""}`}>
                {hasSearchFilters ? (
                  <Button
                    className="import-search-clear"
                    size="small"
                    variant="text"
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setSearchType("");
                      setSearchDateFrom("");
                      setSearchDateTo("");
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
                <Button
                  className="import-search-toggle"
                  size="small"
                  variant={searchOpen ? "contained" : "outlined"}
                  type="button"
                  startIcon={<SearchIcon fontSize="small" />}
                  onClick={() => setSearchOpen((value) => !value)}
                >
                  Search
                </Button>
              </Box>
            </Box>
            <Collapse in={searchOpen}>
              <Box className="import-search-panel">
                <TextField
                  className="import-search-query-field"
                  size="small"
                  placeholder="Search amount, merchant, note..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery ? (
                        <InputAdornment position="end">
                          <IconButton
                            className="import-search-query-clear"
                            size="small"
                            type="button"
                            aria-label="Clear search text"
                            title="Clear search text"
                            onClick={() => setSearchQuery("")}
                            edge="end"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                />
                <TextField
                  select
                  size="small"
                  label="Type"
                  value={searchType}
                  slotProps={{ inputLabel: { shrink: true } }}
                  onChange={(event) => setSearchType(event.target.value as TransactionType | "")}
                >
                  <MenuItem value="">All types</MenuItem>
                  {Object.entries(transactionTypeLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <Box className="import-search-date-range">
                  <DatePickerField
                    className="import-filter-date-field"
                    size="small"
                    label="From"
                    value={searchDateFrom}
                    onChange={setSearchDateFrom}
                  />
                  <DatePickerField
                    className="import-filter-date-field"
                    size="small"
                    label="To"
                    value={searchDateTo}
                    onChange={setSearchDateTo}
                  />
                </Box>
              </Box>
            </Collapse>
            </Box>
            {!filteredRows.length ? <p className="status">No rows match this filter.</p> : null}
            {paginatedRows.length ? (
              <Box className="import-rows" style={{ "--import-amount-ch": `${amountColumnCh}ch` } as CSSProperties}>
                {paginatedRows.map((row) => (
                  <ImportRowEditor
                    key={row.id}
                    row={row}
                    group={group}
                    batchSourceAccountId={batch?.source_account_id}
                    accounts={accounts}
                    members={members}
                    categories={categories}
                    tags={tags}
                    isUpdating={updateRowMutation.isPending || updateSourceAccountMutation.isPending || addCurrencyMutation.isPending || applyMutation.isPending || stopMutation.isPending}
                    isBatchApplied={Boolean(appliedResult || isBatchCompleted(batch?.status))}
                    onAddCurrency={(code) => {
                      void addCurrencyAndRevalidateRows(code);
                    }}
                    onUpdate={(rowId, payload) => {
                      setStatus("");
                      updateRowMutation.mutate({ rowId, payload });
                    }}
                  />
                ))}
              </Box>
            ) : null}
          </Box>
        ) : batchId && !isBatchFailed(batch?.status) && !appliedResult && !isProcessingImport && !batchQuery.isFetching ? (
          <p className="status">No preview rows returned.</p>
        ) : null}
        </Box>
        ) : null}

        {appliedResult || rows.length || status ? (
          <Box className="import-panel-footer">
            {status ? <p className="status error">{status}</p> : null}
            {appliedResult ? (
              <>
                <span className="import-footer-summary import-footer-summary-complete">
                  Imported: {completedAppliedCount} applied
                  {completedSkippedCount !== null ? `, ${completedSkippedCount} skipped` : ""}.
                </span>
                <Button className="import-apply-button" variant="contained" type="button" onClick={close}>
                  Done
                </Button>
              </>
            ) : rows.length ? (
              <>
                <span className="import-footer-summary import-footer-summary-pending">
                  {importableRows} of {rows.length} will be imported
                  {rowFilterCounts.ignored ? ` · ${rowFilterCounts.ignored} ignored` : ""}
                  {rowFilterCounts.duplicate ? ` · ${rowFilterCounts.duplicate} duplicate${rowFilterCounts.duplicate === 1 ? "" : "s"}` : ""}
                </span>
                {activeSection === "records" && filteredRows.length ? (
                  <Box className="import-footer-pagination">
                    <TextField
                      select
                      className="import-page-size-field"
                      size="small"
                      label="Per page"
                      value={rowsPerPage}
                      slotProps={{ inputLabel: { shrink: true }, select: { inputProps: { "aria-label": "Rows per page" } } }}
                      onChange={(event) => {
                        const nextRowsPerPage = Number(event.target.value);
                        setRowsPerPage(importRowsPerPageOptions.includes(nextRowsPerPage as (typeof importRowsPerPageOptions)[number]) ? nextRowsPerPage : defaultImportRowsPerPage);
                      }}
                    >
                      {importRowsPerPageOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      className="import-page-prev-button"
                      size="small"
                      variant="outlined"
                      type="button"
                      disabled={visiblePage <= 1}
                      onClick={() => changePage(Math.max(1, visiblePage - 1))}
                    >
                      Previous
                    </Button>
                    <span>{visiblePage} / {totalPages}</span>
                    <Button
                      className="import-page-next-button"
                      size="small"
                      variant="outlined"
                      type="button"
                      disabled={visiblePage >= totalPages}
                      onClick={() => changePage(Math.min(totalPages, visiblePage + 1))}
                    >
                      Next
                    </Button>
                  </Box>
                ) : null}
                <Button
                  className="import-apply-button"
                  variant="contained"
                  type="button"
                  disabled={!canApply || updateSourceAccountMutation.isPending || applyMutation.isPending}
                  startIcon={isApplyingImport ? <CircularProgress className="import-apply-spinner" size={16} color="inherit" /> : undefined}
                  onClick={() => applyMutation.mutate()}
                >
                  {isApplyingImport ? "Applying import" : `Apply import${importableRows ? ` · ${importableRows}` : ""}`}
                </Button>
              </>
            ) : null}
          </Box>
        ) : null}
        </>
      )}
    </SidePanel>
    {historyDialogOpen ? (
    <SidePanel
      ariaLabelledBy="importHistoryTitle"
      className="import-history-panel"
      contentClassName="import-history-panel-content"
      onClose={() => setHistoryDialogOpen(false)}
    >
      {({ close }) => (
        <>
        <Box className="records-modal-toolbar">
          <h2 id="importHistoryTitle">Import history</h2>
          <Box className="import-history-toolbar-actions">
            {recentBatches.length ? (
              <Button
                className="import-history-remove-all"
                size="small"
                variant="outlined"
                color="error"
                type="button"
                disabled={deleteBatchMutation.isPending || deleteAllBatchesMutation.isPending}
                onClick={deleteAllBatches}
              >
                Remove all
              </Button>
            ) : null}
            <IconButton className="records-icon-button" type="button" aria-label="Close import history" title="Close" onClick={close}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </IconButton>
          </Box>
        </Box>
        {importBatchesQuery.isLoading ? <p className="status">Loading import history...</p> : null}
        {!importBatchesQuery.isLoading && !recentBatches.length ? <p className="status">No imports yet.</p> : null}
        {recentBatches.length ? (
          <Box className="import-history-list">
            {recentBatches.map((recentBatch, index) => {
              const count = rowCount(recentBatch);
              const sourceAccount = accounts.find((account) => account.id === recentBatch.source_account_id);
              const isDeleting =
                (deleteBatchMutation.isPending && deleteBatchMutation.variables === recentBatch.id) ||
                deleteAllBatchesMutation.isPending;
              const isRollingBack = rollbackBatchMutation.isPending && rollbackBatchMutation.variables?.rollbackBatchId === recentBatch.id;
              const isBusy = isDeleting || isRollingBack;
              return (
                <Box className="import-history-row" key={recentBatch.id}>
                  <Button
                    className="import-recent-button import-history-select"
                    variant="text"
                    type="button"
                    disabled={isBusy}
                    onClick={() => selectBatch(recentBatch)}
                  >
                    <span className="import-recent-copy">
                      <span>{recentBatchActionLabel(recentBatch, index === 0)}</span>
                      <small>
                        {formatBatchDate(recentBatch)} · {batchStatusLabel(recentBatch)}
                        {count !== null ? ` · ${count} rows` : ""}
                        {recentBatch.original_filename ? ` · ${recentBatch.original_filename}` : ""}
                        {sourceAccount ? ` · ${accountHistoryLabel(sourceAccount, members)}` : ""}
                      </small>
                    </span>
                  </Button>
                  {isBatchApplied(recentBatch.status) ? (
                    <Tooltip title="Roll back import">
                      <span>
                        <IconButton
                          className="records-icon-button"
                          type="button"
                          aria-label="Roll back import"
                          disabled={isBusy}
                          onClick={() => rollbackBatch(recentBatch)}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                  <Tooltip title="Delete import record">
                    <span>
                      <IconButton
                        className="records-icon-button danger"
                        type="button"
                        aria-label="Delete import record"
                        disabled={isBusy}
                        onClick={() => deleteBatch(recentBatch)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        ) : null}
        </>
      )}
    </SidePanel>
    ) : null}
    <Dialog
      open={Boolean(rollbackTarget)}
      onClose={() => {
        if (!rollbackBatchMutation.isPending) setRollbackTarget(null);
      }}
      aria-labelledby="rollbackImportTitle"
      slotProps={{ paper: { className: "modal records-modal import-rollback-modal" } }}
    >
      <DialogContent className="records-modal-content">
        <Box className="records-modal-toolbar">
          <h2 id="rollbackImportTitle">Roll back import</h2>
          <IconButton className="records-icon-button" type="button" aria-label="Close rollback dialog" title="Close" disabled={rollbackBatchMutation.isPending} onClick={() => setRollbackTarget(null)}>
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </IconButton>
        </Box>
        <p className="confirm-copy">Choose what to delete from this applied import.</p>
        <Box className="import-file-options">
          <FormControlLabel control={<Switch checked={rollbackSections.records} onChange={(event) => setRollbackSections((current) => ({ ...current, records: event.target.checked }))} />} label="records" />
          <FormControlLabel control={<Switch checked={rollbackSections.categories} onChange={(event) => setRollbackSections((current) => ({ ...current, categories: event.target.checked }))} />} label="categories" />
          <FormControlLabel control={<Switch checked={rollbackSections.accounts} onChange={(event) => setRollbackSections((current) => ({ ...current, accounts: event.target.checked }))} />} label="accounts" />
        </Box>
        <Box className="records-modal-actions">
          <Button variant="outlined" type="button" disabled={rollbackBatchMutation.isPending} onClick={() => setRollbackTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            type="button"
            disabled={rollbackBatchMutation.isPending || !rollbackTarget || !Object.values(rollbackSections).some(Boolean)}
            onClick={() => {
              if (!rollbackTarget) return;
              rollbackBatchMutation.mutate({ rollbackBatchId: rollbackTarget.id, sections: rollbackSections });
            }}
          >
            Roll back
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
	    </>
  );
}

function mergeImportBatchPatch(current: ImportBatch | undefined, patch: ImportBatch): ImportBatch {
  if (!current) return patch;
  return {
    ...current,
    ...patch,
    rows: mergeImportCollection(current.rows, patch.rows),
    category_rows: mergeImportCollection(current.category_rows, patch.category_rows),
    account_rows: mergeImportCollection(current.account_rows, patch.account_rows),
    currency_rows: mergeImportCollection(current.currency_rows, patch.currency_rows),
    imported_categories_count: current.imported_categories_count,
    imported_accounts_count: current.imported_accounts_count,
  };
}

function mergeImportCollection<T extends { id?: string | null }>(current: T[] | null | undefined, patch: T[] | null | undefined): T[] {
  if (!patch?.length) return current ? [...current] : [];
  if (!current?.length) return [...patch];
  const merged = [...current];
  for (const item of patch) {
    if (!item.id) {
      merged.push(item);
      continue;
    }
    const index = merged.findIndex((existing) => existing.id === item.id);
    if (index >= 0) {
      merged[index] = { ...merged[index], ...item };
      continue;
    }
    merged.push(item);
  }
  return merged;
}
